import { ago, CustomerRunStatus, money, QuoteStatus, RfqStatus } from "@lumberset/takeoff-ui";
import { Button, Container, EmptyState, SectionHeader, Stat, Table } from "@lumberset/ui";
import { ArrowUpTray, Buildings } from "@medusajs/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Crumbs } from "../../components/crumbs";
import { GettingStarted } from "../../components/getting-started";
import { HomeBilling } from "../../components/home-billing";
import { billingSummaryQuery, projectsQuery, quotesQuery, rfqsQuery } from "../../lib/queries";

interface HomeSearch {
  /** `add` from the takeoff ready email's link; `saved` or `canceled` back from Stripe. */
  card?: "add" | "saved" | "canceled";
}

export const Route = createFileRoute("/_app/")({
  validateSearch: (search: Record<string, unknown>): HomeSearch =>
    search.card === "add" || search.card === "saved" || search.card === "canceled"
      ? { card: search.card }
      : {},
  loader: ({ context: { queryClient, yard } }) =>
    Promise.all([
      queryClient.ensureQueryData(projectsQuery()),
      // A Builder has RFQs where a Yard has Quotes.
      yard.kind === "builder"
        ? queryClient.ensureQueryData(rfqsQuery)
        : queryClient.ensureQueryData(quotesQuery),
      queryClient.ensureQueryData(billingSummaryQuery).catch(() => null),
    ]),
  component: Home,
});

const inProgress = new Set(["queued", "running", "in_review"]);

function Home() {
  const { user, yard } = Route.useRouteContext();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { data: projects } = useSuspenseQuery(projectsQuery());
  const firstName = user.name.split(" ")[0];
  const checklist = (
    <>
      <HomeBilling
        workspaceId={yard.id}
        card={search.card}
        onHandled={() => void navigate({ to: "/", search: {}, replace: true })}
      />
      <GettingStarted workspace={yard} />
    </>
  );

  if (projects.projects.length === 0) {
    return (
      <>
        <Crumbs trail={[{ label: "Home" }]} />
        <Container className="p-0">
          <EmptyState
            className="py-24"
            icon={<ArrowUpTray />}
            title={`Welcome to LumberSet, ${firstName}`}
            description={
              yard.kind === "builder"
                ? "Create a project, upload the drawings, and get back a takeoff where every number shows its work. Budget it, then send it to the yards you choose. Your first one is free."
                : "Create a project, upload the drawings your customer sent, and get back a takeoff where every number shows its work. Your first one is free."
            }
            action={
              <Button asChild size="large">
                <Link to="/projects" search={{ new: true }}>
                  Create your first project
                </Link>
              </Button>
            }
          />
        </Container>
        {checklist}
      </>
    );
  }

  return yard.kind === "builder" ? (
    <BuilderHome checklist={checklist} />
  ) : (
    <YardHome checklist={checklist} />
  );
}

function RecentProjects() {
  const navigate = useNavigate();
  const { yard } = Route.useRouteContext();
  const { data: projects } = useSuspenseQuery(projectsQuery());
  // A Builder's project has a site, not a Customer to quote.
  const subtitle = (project: {
    customerName: string | null;
    siteAddress: { line1?: string } | null;
  }) =>
    yard.kind === "builder"
      ? (project.siteAddress?.line1 ?? "No site address")
      : (project.customerName ?? "No customer yet");

  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Recent projects"
        actions={
          <Button asChild size="small" variant="secondary">
            <Link to="/projects" search={{}}>
              View all
            </Link>
          </Button>
        }
      />
      <Table>
        <Table.Body>
          {projects.projects.slice(0, 6).map((project) => (
            <Table.Row
              key={project.id}
              data-clickable="true"
              onClick={() => navigate({ to: "/projects/$id", params: { id: project.id } })}
            >
              <Table.Cell>
                <div className="flex items-center gap-x-3">
                  <Buildings className="shrink-0 text-ui-fg-muted" />
                  <div className="min-w-0">
                    <div className="txt-compact-small-plus truncate text-ui-fg-base">
                      {project.name}
                    </div>
                    <div className="txt-compact-xsmall truncate text-ui-fg-muted">
                      {subtitle(project)}
                    </div>
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell className="w-[1%] whitespace-nowrap">
                <CustomerRunStatus status={project.runStatus} />
              </Table.Cell>
              <Table.Cell className="w-[1%] whitespace-nowrap text-right max-sm:hidden">
                {ago(project.updatedAt)}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Container>
  );
}

function YardHome({ checklist }: { checklist: ReactNode }) {
  const navigate = useNavigate();
  const { yard } = Route.useRouteContext();
  const { data: projects } = useSuspenseQuery(projectsQuery());
  const { data: quotes } = useSuspenseQuery(quotesQuery);

  const active = projects.projects.filter((project) => project.status === "active");
  const inFlight = projects.projects.filter(
    (project) => project.runStatus && inProgress.has(project.runStatus),
  );
  const open = quotes.quotes.filter(
    (quote) => quote.status === "sent" || quote.status === "viewed",
  );
  const openValue = open.reduce((sum, quote) => sum + quote.totalCents, 0);
  const won = quotes.quotes.filter((quote) => quote.status === "accepted");

  return (
    <>
      <Crumbs trail={[{ label: "Home" }]} />
      {checklist}

      <Container className="grid grid-cols-2 divide-ui-border-base p-0 max-lg:divide-y lg:grid-cols-4 lg:divide-x">
        <Stat label="Active projects" value={active.length} hint={yard.name} />
        <Stat label="Takeoffs in progress" value={inFlight.length} hint="Reading or in review" />
        <Stat label="Open quotes" value={open.length} hint={money(openValue)} />
        <Stat
          label="Quotes accepted"
          value={won.length}
          hint={money(won.reduce((sum, quote) => sum + quote.totalCents, 0))}
        />
      </Container>

      <RecentProjects />

      {quotes.quotes.length > 0 ? (
        <Container className="divide-y p-0">
          <SectionHeader
            title="Recent quotes"
            actions={
              <Button asChild size="small" variant="secondary">
                <Link to="/quotes">View all</Link>
              </Button>
            }
          />
          <Table>
            <Table.Body>
              {quotes.quotes.slice(0, 6).map((quote) => (
                <Table.Row
                  key={quote.id}
                  data-clickable="true"
                  onClick={() => navigate({ to: "/quotes/$id", params: { id: quote.id } })}
                >
                  <Table.Cell>
                    <div className="txt-compact-small-plus text-ui-fg-base">
                      #{quote.number}{" "}
                      <span className="font-normal text-ui-fg-subtle">{quote.projectName}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="w-[1%] whitespace-nowrap">
                    <QuoteStatus status={quote.status} />
                  </Table.Cell>
                  <Table.Cell className="tabular w-[1%] whitespace-nowrap text-right text-ui-fg-base">
                    {money(quote.totalCents, quote.currency)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Container>
      ) : null}
    </>
  );
}

function BuilderHome({ checklist }: { checklist: ReactNode }) {
  const navigate = useNavigate();
  const { yard } = Route.useRouteContext();
  const { data: projects } = useSuspenseQuery(projectsQuery());
  const { data: rfqs } = useSuspenseQuery(rfqsQuery);

  const active = projects.projects.filter((project) => project.status === "active");
  const inFlight = projects.projects.filter(
    (project) => project.runStatus && inProgress.has(project.runStatus),
  );
  const ready = projects.projects.filter((project) => project.runStatus === "released");
  const open = rfqs.rfqs.filter((rfq) => rfq.status === "sent");
  const replies = rfqs.rfqs.reduce((sum, rfq) => sum + rfq.recipients.responded, 0);
  const awaiting = open.reduce(
    (sum, rfq) => sum + rfq.recipients.sent + rfq.recipients.viewed + rfq.recipients.pending,
    0,
  );

  return (
    <>
      <Crumbs trail={[{ label: "Home" }]} />
      {checklist}

      <Container className="grid grid-cols-2 divide-ui-border-base p-0 max-lg:divide-y lg:grid-cols-4 lg:divide-x">
        <Stat label="Active projects" value={active.length} hint={yard.name} />
        <Stat label="Takeoffs in progress" value={inFlight.length} hint="Reading or in review" />
        <Stat label="Takeoffs ready" value={ready.length} hint="Budget, export or send" />
        <Stat
          label="Open RFQs"
          value={open.length}
          hint={`${replies} ${replies === 1 ? "reply" : "replies"}, ${awaiting} waiting`}
        />
      </Container>

      <RecentProjects />

      {rfqs.rfqs.length > 0 ? (
        <Container className="divide-y p-0">
          <SectionHeader
            title="Recent RFQs"
            actions={
              <Button asChild size="small" variant="secondary">
                <Link to="/rfqs">View all</Link>
              </Button>
            }
          />
          <Table>
            <Table.Body>
              {rfqs.rfqs.slice(0, 6).map((rfq) => (
                <Table.Row
                  key={rfq.id}
                  data-clickable="true"
                  onClick={() => navigate({ to: "/rfqs/$id", params: { id: rfq.id } })}
                >
                  <Table.Cell>
                    <div className="txt-compact-small-plus truncate text-ui-fg-base">
                      {rfq.projectName}
                    </div>
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {rfq.recipients.responded} of {rfq.recipients.total} replied
                    </div>
                  </Table.Cell>
                  <Table.Cell className="w-[1%] whitespace-nowrap">
                    <RfqStatus status={rfq.status} />
                  </Table.Cell>
                  <Table.Cell className="tabular w-[1%] whitespace-nowrap text-right text-ui-fg-base">
                    {rfq.lowestTotalCents !== null ? money(rfq.lowestTotalCents) : "-"}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Container>
      ) : null}
    </>
  );
}
